import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import api from '../utils/api';

interface SessionDetails {
  customer_email: string;
  amount_total: number;
  subscription: {
    current_period_end: number;
  };
}

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchSessionDetails = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch real session details from backend
        const response = await api.get<SessionDetails>(`/api/stripe/session/${sessionId}`);
        setSessionDetails(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching session details:', error);
        setLoading(false);
      }
    };

    fetchSessionDetails();
  }, [sessionId]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-lg">Processing your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-900 text-white">
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center">
          {/* Success Icon */}
          <div className="mx-auto w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-8">
            <CheckCircleIcon className="w-16 h-16 text-green-600" />
          </div>

          {/* Success Message */}
          <h1 className="text-4xl font-bold mb-4">
            Welcome to BlockBoard! 🎉
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Your subscription has been successfully activated
          </p>

          {/* Subscription Details */}
          {sessionDetails && (
            <div className="bg-background-800 rounded-lg p-6 mb-8 max-w-md mx-auto">
              <h3 className="text-lg font-semibold mb-4">Subscription Details</h3>
              <div className="space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-400">Plan:</span>
                  <span>BlockBoard Starter</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span>${(sessionDetails.amount_total / 100).toFixed(2)}/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Trial Period:</span>
                  <span>14 days free</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Next Billing:</span>
                  <span>{new Date(sessionDetails.subscription.current_period_end * 1000).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="bg-background-800 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">What's Next?</h3>
            <div className="grid md:grid-cols-3 gap-4 text-left">
              <div className="space-y-2">
                <div className="w-8 h-8 bg-accent-600 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <h4 className="font-medium">Set Up Your Property Group</h4>
                <p className="text-sm text-gray-400">Configure your properties and addresses</p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 bg-accent-600 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <h4 className="font-medium">Invite Residents</h4>
                <p className="text-sm text-gray-400">Add residents to your property management system</p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 bg-accent-600 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <h4 className="font-medium">Start Managing</h4>
                <p className="text-sm text-gray-400">Begin using all the powerful features</p>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            className="group bg-accent-600 hover:bg-accent-700 text-white font-bold py-4 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
          >
            <span>Continue to Dashboard</span>
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </button>

          {/* Support */}
          <p className="text-sm text-gray-400 mt-8">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@blockboardapp.com" className="text-accent-400 hover:text-accent-300">
              support@blockboardapp.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;